import React, { useState, useEffect } from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";
import "./GarageSpacesControl.css"; // ⬅️ keep this for numeric spinner styling

export interface GarageSpacesControlProps {
  initialValue?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Themed numeric control for "Garage Spaces".
 * Automatically adapts to system light/dark mode.
 */
export const GarageSpacesControl: React.FC<GarageSpacesControlProps> = ({
  initialValue = 2,
  onChange,
  min = 0,
  max = 6,
  step = 1,
}) => {
  const theme = useSystemTheme();

  // Pull colors dynamically from current CSS theme variables
  const textColor = "var(--text-color)";
  const borderColor = "var(--border-color)";
  const inputBg = "var(--input-bg)";
  const labelColor = "var(--muted-text-color)";

  const [value, setValue] = useState<number>(initialValue);
  const [error, setError] = useState<string>("");

  // Keep state synced with parent resets
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Validate and emit
  useEffect(() => {
    if (value < min) setError(`Must be ≥ ${min}`);
    else if (value > max) setError(`Must be ≤ ${max}`);
    else {
      setError("");
      onChange(value);
    }
  }, [value, min, max, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/[^0-9]/g, "");
    const num = parseInt(cleaned, 10);
    setValue(Number.isNaN(num) ? 0 : num);
  };

  // Shared themed styling
  const inputClass =
    "w-full text-center rounded shadow-sm focus:outline-none [appearance:auto] transition-all duration-150";
  const inputStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <Label htmlFor="garageSpacesInput" className={labelClass} style={labelStyle}>
        Garage Spaces
      </Label>

      <Input
        id="garageSpacesInput"
        type="number"
        inputMode="numeric"
        value={value.toString()}
        onChange={handleChange}
        step={step}
        min={min}
        max={max}
        className={inputClass}
        style={inputStyle}
      />

      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};